// src/features/quotation/components/QuotationPreview.jsx
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Box,
  Typography,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
} from "@mui/material";
import { Print, Close, Download, Receipt, Send } from "@mui/icons-material";
import {
  formatCurrency,
  getStatusLabel,
  getStatusColor,
} from "../utils/quotationHelpers";
import QuotationStatusBadge from "./QuotationStatusBadge";

const QuotationPreview = ({
  open,
  onClose,
  quotation,
  onConvert,
  onSend,
  readOnly = true,
}) => {
  if (!quotation) return null;

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "#d9d9d9",
      sent: "#1890ff",
      viewed: "#13c2c2",
      approved: "#52c41a",
      rejected: "#ff4d4f",
      expired: "#faad14",
      converted: "#722ed1",
    };
    return colors[status] || "#d9d9d9";
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Quotation #{quotation.number}</Typography>
          <Box>
            <IconButton onClick={() => window.print()} size="small">
              <Print />
            </IconButton>
            <IconButton size="small">
              <Download />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Paper sx={{ p: 4, bgcolor: "#fafafa" }}>
          {/* Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
            <Box>
              <Typography variant="h5" fontWeight="bold" color="primary">
                Hashtag Photography Studio
              </Typography>
              <Typography variant="body2" color="text.secondary">
                123 Photo Street, City, State
              </Typography>
              <Typography variant="body2" color="text.secondary">
                contact@hashtagphotography.com
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Phone: (555) 123-4567
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="h6" fontWeight="bold" color="primary">
                QUOTATION
              </Typography>
              <Typography variant="body2">#{quotation.number}</Typography>
              <Box sx={{ mt: 1 }}>
                <QuotationStatusBadge status={quotation.status} />
              </Box>
            </Box>
          </Box>

          {/* Client Info */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Bill To:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {quotation.clientName}
              </Typography>
              <Typography variant="body2">{quotation.clientEmail}</Typography>
              {quotation.clientPhone && (
                <Typography variant="body2">{quotation.clientPhone}</Typography>
              )}
              {quotation.clientCompany && (
                <Typography variant="body2" color="text.secondary">
                  {quotation.clientCompany}
                </Typography>
              )}
              {quotation.clientAddress && (
                <Typography variant="body2" color="text.secondary">
                  {quotation.clientAddress.street}
                  <br />
                  {quotation.clientAddress.city},{" "}
                  {quotation.clientAddress.state}{" "}
                  {quotation.clientAddress.zipCode}
                  <br />
                  {quotation.clientAddress.country}
                </Typography>
              )}
            </Grid>
            <Grid item xs={6} sx={{ textAlign: "right" }}>
              <Typography variant="subtitle2" color="text.secondary">
                Quotation Details:
              </Typography>
              <Typography variant="body2">
                Date: {formatDate(quotation.date)}
              </Typography>
              <Typography variant="body2">
                Valid Until: {formatDate(quotation.validUntil)}
              </Typography>
              {quotation.paymentTerms && (
                <Typography variant="body2">
                  Terms: {quotation.paymentTerms}
                </Typography>
              )}
              <Typography variant="body2">
                Currency: {quotation.currency}
              </Typography>
            </Grid>
          </Grid>

          {/* Items Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="right">Discount</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quotation.items.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {item.description}
                      {item.notes && (
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          Note: {item.notes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(item.unitPrice, quotation.currency)}
                    </TableCell>
                    <TableCell align="right">
                      {item.discount ? `₹{item.discount}%` : "-"}
                    </TableCell>
                    <TableCell align="right" fontWeight="bold">
                      {formatCurrency(
                        item.total || item.quantity * item.unitPrice,
                        quotation.currency,
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Summary */}
          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Box sx={{ width: 300 }}>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="body2">Subtotal:</Typography>
                <Typography variant="body2">
                  {formatCurrency(quotation.subtotal, quotation.currency)}
                </Typography>
              </Box>
              {quotation.discount > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="error">
                    Discount (
                    {quotation.discountType === "percentage"
                      ? `₹{quotation.discount}%`
                      : "Fixed"}
                    ):
                  </Typography>
                  <Typography variant="body2" color="error">
                    -
                    {formatCurrency(
                      quotation.discountAmount,
                      quotation.currency,
                    )}
                  </Typography>
                </Box>
              )}
              {quotation.taxRate > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2">
                    Tax ({quotation.taxRate}%):
                  </Typography>
                  <Typography variant="body2">
                    {formatCurrency(quotation.taxAmount, quotation.currency)}
                  </Typography>
                </Box>
              )}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="h6" fontWeight="bold">
                  Total:
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {formatCurrency(quotation.total, quotation.currency)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Notes & Terms */}
          {quotation.notes && (
            <Box sx={{ mt: 4 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Notes:
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {quotation.notes}
              </Typography>
            </Box>
          )}

          {quotation.termsAndConditions && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Terms & Conditions:
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {quotation.termsAndConditions}
              </Typography>
            </Box>
          )}

          {/* Signature */}
          {quotation.signature && (
            <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #e0e0e0" }}>
              <Typography variant="body2">
                Signed by: {quotation.signature}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Date: {formatDate(quotation.signatureDate)}
              </Typography>
            </Box>
          )}

          {/* Footer */}
          <Box
            sx={{
              mt: 4,
              pt: 2,
              borderTop: "1px solid #e0e0e0",
              textAlign: "center",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              This is a computer-generated quotation. Please verify all details.
            </Typography>
            <br />
            <Typography variant="caption" color="text.secondary">
              Valid for 15 days from date of issue.
            </Typography>
          </Box>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {quotation.status === "draft" && onSend && (
          <Button
            variant="contained"
            startIcon={<Send />}
            onClick={() => onSend && onSend(quotation.id)}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            Send to Client
          </Button>
        )}
        {quotation.status === "approved" &&
          onConvert &&
          !quotation.isConverted && (
            <Button
              variant="contained"
              startIcon={<Receipt />}
              onClick={() => onConvert && onConvert(quotation.id)}
              sx={{
                background: "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)",
              }}
            >
              Convert to Invoice
            </Button>
          )}
      </DialogActions>
    </Dialog>
  );
};

export default QuotationPreview;
